import {
  BaseEvent,
  EventType,
  isPartialStoppage,
  Stoppage,
  StoppageKind,
  useEventIsSplit
} from '@domain/shifthandover'
import {EquipmentIdType} from '@hconnect/common/types'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import React, {FC} from 'react'

import {EventFormState} from '../types'

import {StoppageForm} from './eventForm'

export const getIsStoppageDateEditable = (
  isCreateMode: boolean | undefined,
  item: Partial<BaseEvent>
) => {
  if (!isPartialStoppage(item)) return false
  if (isCreateMode) return !!(item.stoppageStart || item.stoppageEnd)
  return !!item.pxtrendStoppageId
}

// priority between the flags is described in this task HCP-101883
const handleFunctionalLocationRequired = (
  type?: EventType,
  isFunctionalLocationOptional?: boolean,
  isEquipmentAvailable?: boolean
) => {
  return (
    isEquipmentAvailable && !isFunctionalLocationOptional && type !== StoppageKind.PlannedStoppage
  )
}

const emptyMap = new Map()

type StoppageFormWrapperProps = {
  plantId: string
  item: Partial<Stoppage>
  isCreateMode?: boolean
  children: React.ReactNode
  hideStoppageDateType?: boolean
  isEquipmentAvailable?: boolean
  updateMultiPart: (delta: Partial<Stoppage>) => void
  equipmentIdType?: EquipmentIdType
  autocompleteProcessStage?: boolean
} & Omit<EventFormState, 'item'>

export const StoppageFormWrapper: FC<StoppageFormWrapperProps> = ({
  item,
  hideStoppageDateType,
  children,
  plantId,
  isEquipmentAvailable,
  isCreateMode,
  originalItem,
  updateMultiPart,
  equipmentIdType,
  autocompleteProcessStage,
  validationError,
  showValidation
}) => {
  const isStoppageSubmitSupported = useCommonFeatureFlag(FeatureFlagKey.SubmitStoppage)
  const disableStartEndChange = getIsStoppageDateEditable(isCreateMode, originalItem)
  const isSplittingStoppage =
    isPartialStoppage(originalItem) && !!originalItem.originalSplitStoppageId
  const isFunctionalLocationOptional = useCommonFeatureFlag(
    FeatureFlagKey.FunctionalLocationOptional,
    {
      plantId
    }
  )
  const isFunctionalLocationRequired = handleFunctionalLocationRequired(
    item.eventType,
    isFunctionalLocationOptional,
    isEquipmentAvailable
  )

  const {data: isEventSplit} = useEventIsSplit({plantId, eventId: item.id})

  return (
    <StoppageForm
      item={item}
      hideStoppageDateType={hideStoppageDateType || disableStartEndChange}
      updateMultiPart={updateMultiPart}
      validationError={showValidation ? validationError : emptyMap}
      disabledFields={{
        stoppageStart: disableStartEndChange,
        stoppageEnd: disableStartEndChange,
        upmDepartmentId: isSplittingStoppage || isEventSplit,
        stoppageType: isSplittingStoppage || isEventSplit,
        ...(isStoppageSubmitSupported && item.isSubmitted
          ? {
              stoppageStart: true,
              stoppageEnd: true,
              upmDepartmentId: true,
              stoppageCode: true,
              eventType: true,
              stoppageType: true,
              stoppageDateType: true
            }
          : {})
      }}
      equipmentIdType={equipmentIdType}
      autocompleteProcessStage={autocompleteProcessStage}
      isFunctionalLocationRequired={isFunctionalLocationRequired}
      plantId={plantId}
    >
      {children}
    </StoppageForm>
  )
}
